'use client';

import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useTranslations } from 'next-intl';

import { Logo } from '@/components/logo';
import { dur, ease, sceneItem, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';
import { AD_INTEGRATIONS, STORE_INTEGRATIONS } from '@/lib/site-integrations';

import './platforms.css';

/**
 * Entegrasyonlar — bağlantı haritası (14 Eki 2026'da baştan yazıldı).
 *
 * Eski hâli kart ızgarasıydı: beş reklam kanalı hücrelerde, mağazalar altta
 * bir bantta. Kullanıcı "hiç güzel olmadı, kreatif özel bir şey yap" dedi.
 *
 * ── Ne anlatıyor ──────────────────────────────────────────────────────────
 *
 * Bölümün tek cümlesi "her şey tek panelde birleşiyor". Harita bunu ÇİZİYOR:
 * solda reklam kanalları, sağda mağazalar, ortada Mentorivo; her platform
 * göbeğe kendi hattıyla bağlı ve hatlardan göbeğe veri akıyor. Kart ızgarası
 * "16 ayrı şey" diyordu, harita "16 kaynak, tek varış noktası" diyor.
 *
 * İki sütunun içeriği MERKEZE yaslı (reklam satırlarında metin sağa, mağaza
 * satırlarında sola dayanıyor): göz kenardan göbeğe doğru akıyor ve hatlar
 * yalnız göbek sütununun içinde kalıyor — ekran ne kadar genişlerse genişlesin
 * kısa ve sıkı.
 *
 * ── Hareket neyi açıklıyor ────────────────────────────────────────────────
 *
 * 1. Paket akışı — hatlar boyunca göbeğe doğru küçük yeşil noktalar: "canlı
 *    veri" (ürünün kendisi senkron saati değil izleme ritmiyle tazeleniyor,
 *    `@mentorivo/shared/live`). Seyrek ve sakin: ~0.8 sn'de bir, tek nokta.
 *    Yön her zaman platformdan göbeğe — veri içeri akıyor.
 * 2. Seçim — bir satırın üzerine gelince / odaklanınca / dokununca o hat
 *    yeşil yanıyor, diğer satırlar ve hatlar geri çekiliyor, paketler yalnız
 *    o hattan ve daha sık akıyor, göbek bir kez halka atıyor ("bağlandı"),
 *    alttaki gösterge o platformun künyesine geçiyor (bulanıktan netleşerek).
 *
 * Bölüm sayfa açılır açılmaz son hâlinde (kullanıcı kararı, 9 Eyl 2026 —
 * kaydırmayla açılan giriş yok). Hatlar ölçümden sonra çiziliyor, yalnız
 * opaklıkla beliriyor.
 *
 * ── Kısıtlar ──────────────────────────────────────────────────────────────
 *
 * • Paketler yalnız `transform` (x/y) ile gidiyor: eğri JS'te örneklenip
 *   anahtar karelere çevriliyor — `offset-path`/`stroke-dashoffset` değil
 *   (DESIGN.md §1). Vurgu iki katmanlı hat + opaklık geçişi.
 * • Azaltılmış hareket: paket akışı hiç başlamıyor (karar efektte; paket
 *   listesi sunucuda ve ilk çizimde zaten boş — hidrasyon ayrışmıyor).
 *   Halka ve gösterge geçişi MotionConfig'le yalnız opaklığa iniyor.
 * • Akış görüş alanı dışında duruyor (`useInView`, once DEĞİL — bu bir giriş
 *   animasyonu değil, arka planda dönen döngünün duraklatılması).
 * • 900px altında harita dikey: göbek üstte, listeler alt alta, hat ve paket
 *   yok (yan yana sütun olmadan hattın gideceği yer yok). Seçim ve gösterge
 *   aynı çalışıyor; dokununca gösterge görünür değilse ona kaydırılıyor.
 */

type AdItem = (typeof AD_INTEGRATIONS)[number];
type StoreItem = (typeof STORE_INTEGRATIONS)[number];
type Entry = { group: 'ad'; item: AdItem } | { group: 'store'; item: StoreItem };

const ENTRIES = new Map<string, Entry>([
  ...AD_INTEGRATIONS.map((item): [string, Entry] => [item.key, { group: 'ad', item }]),
  ...STORE_INTEGRATIONS.map((item): [string, Entry] => [item.key, { group: 'store', item }]),
]);

const TOTAL = AD_INTEGRATIONS.length + STORE_INTEGRATIONS.length;

/** Haritanın yan yana çizildiği eşik — CSS'teki `.imap` kuralıyla aynı. */
const WIDE_QUERY = '(min-width: 900px)';

/* --- Geometri ------------------------------------------------------------ */

interface Port {
  key: string;
  x: number;
  y: number;
}

interface Geometry {
  w: number;
  h: number;
  hub: { x: number; y: number; r: number };
  ports: Port[];
}

interface Trace {
  key: string;
  d: string;
  /** Paket anahtar kareleri — eğrinin örneklenmiş noktaları, platformdan göbeğe. */
  xs: number[];
  ys: number[];
  length: number;
}

/**
 * Yelpaze açısı (radyan): hatlar göbeğin çemberine tek noktadan değil, bir
 * yay boyunca giriyor. Beş hat ±32°, on bir hat ±46° — sık yelpaze daha geniş
 * açılmazsa uçlar üst üste biniyor.
 */
const FAN_NARROW = 0.56;
const FAN_WIDE = 0.8;
const SAMPLES = 20;

const round = (n: number) => Math.round(n * 10) / 10;

function cubic(a: number, b: number, c: number, d: number, t: number) {
  const u = 1 - t;
  return u * u * u * a + 3 * u * u * t * b + 3 * u * t * t * c + t * t * t * d;
}

/**
 * Port → göbek çemberi, yatay çıkıp çembere RADYAL giren kübik eğri.
 * Taraf ölçümden (port göbeğin solunda mı) — sağdan sola yazımda sütunlar
 * yer değiştirince hatlar kendiliğinden doğru yöne dönüyor.
 */
function buildTraces(geo: Geometry): Trace[] {
  const { hub } = geo;

  const fan = (ports: Port[], side: 'left' | 'right'): Trace[] => {
    const sorted = [...ports].sort((a, b) => a.y - b.y);
    const mid = (sorted.length - 1) / 2;
    const spread = sorted.length > 6 ? FAN_WIDE : FAN_NARROW;

    return sorted.map((port, index) => {
      const phi = mid === 0 ? 0 : (spread * (index - mid)) / mid;
      const alpha = side === 'left' ? Math.PI - phi : phi;
      const ux = Math.cos(alpha);
      const uy = Math.sin(alpha);
      const end = { x: hub.x + hub.r * ux, y: hub.y + hub.r * uy };
      const reach = Math.abs(end.x - port.x);
      const c1 = { x: port.x + (end.x - port.x) * 0.55, y: port.y };
      const c2 = { x: end.x + ux * reach * 0.35, y: end.y + uy * reach * 0.35 };

      const xs: number[] = [];
      const ys: number[] = [];
      let length = 0;
      for (let step = 0; step <= SAMPLES; step += 1) {
        const t = step / SAMPLES;
        const x = round(cubic(port.x, c1.x, c2.x, end.x, t));
        const y = round(cubic(port.y, c1.y, c2.y, end.y, t));
        if (step > 0) length += Math.hypot(x - xs[step - 1]!, y - ys[step - 1]!);
        xs.push(x);
        ys.push(y);
      }

      return {
        key: port.key,
        d: `M${round(port.x)} ${round(port.y)}C${round(c1.x)} ${round(c1.y)} ${round(c2.x)} ${round(c2.y)} ${round(end.x)} ${round(end.y)}`,
        xs,
        ys,
        length,
      };
    });
  };

  return [
    ...fan(
      geo.ports.filter((port) => port.x < hub.x),
      'left',
    ),
    ...fan(
      geo.ports.filter((port) => port.x >= hub.x),
      'right',
    ),
  ];
}

/* --- Paketler ------------------------------------------------------------ */

interface Packet {
  id: number;
  key: string;
}

/** Ortam akışında yeni paket aralığı; seçili hatta daha sık. */
const AMBIENT_EVERY = 820;
const FOCUS_EVERY = 460;
/** Hız sabit (~140 px/sn): kısa hat kısa sürede biter, uzun hat sürünmez. */
const packetDuration = (length: number) => Math.min(1.7, Math.max(0.9, length / 140));

/* --- Varyantlar ---------------------------------------------------------- */

const frameV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const headV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};

/* Gösterge: bulanıktan netleşiyor (yeni künye "odaklanıyor"), çıkışı kısa. */
const readoutV: Variants = {
  hidden: { opacity: 0, y: 6, filter: 'blur(4px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: springFade(spring.default) },
  exit: { opacity: 0, y: -4, filter: 'blur(2px)', transition: { duration: dur.instant } },
};

export function Platforms() {
  const t = useTranslations('platforms');
  const uid = useId();

  return (
    <section className="platforms" id="platforms" aria-labelledby={`${uid}-title`}>
      {/* Yan sütunlar basılmıyor — .grid-item-8fr `grid-column: 2` ile kendi
          yerleşiyor. Çerçevenin dikey hatları hero'nunkilerin ve SSS'inkilerin
          tam üstünde: beyaz alan sayfanın ızgarasına oturuyor. */}
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div className="platforms-frame" variants={frameV} initial={false} animate="show">
            <motion.div className="platforms-head" variants={headV}>
              <motion.p className="platforms-eyebrow" variants={sceneItem}>
                {t('eyebrow')}
              </motion.p>

              {/* Gerçek h2 — görsele gömülü yazı yok; arama terimleri
                  ("Meta, Google, TikTok, LinkedIn, X … tek panelde") burada. */}
              <motion.h2 className="platforms-title" id={`${uid}-title`} variants={sceneItem}>
                {t.rich('title', { hl })}
              </motion.h2>

              {/* Tek başına alıntılanabilir olgu cümlesi — harita görsel,
                  bilgi burada da düz metin olarak duruyor. */}
              <motion.p className="platforms-lead" variants={sceneItem}>
                {t('lead')}
              </motion.p>
            </motion.div>

            <IntegrationMap />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function IntegrationMap() {
  const t = useTranslations('platforms');
  const uid = useId();
  const reduce = useReducedMotion();

  const mapRef = useRef<HTMLDivElement>(null);
  const hubRef = useRef<HTMLDivElement>(null);
  const readoutRef = useRef<HTMLDivElement>(null);
  const geoKey = useRef('');
  const packetSeq = useRef(0);

  const [active, setActive] = useState<string | null>(null);
  const [wide, setWide] = useState(false);
  const [geo, setGeo] = useState<Geometry | null>(null);
  const [packets, setPackets] = useState<Packet[]>([]);

  const inView = useInView(mapRef, { amount: 0.25 });

  /* Eşik takibi. İlk çizimde `false` — sunucuyla aynı ağaç; hatlar ancak
     tarayıcı genişliği bildikten sonra çiziliyor. */
  useEffect(() => {
    const query = window.matchMedia(WIDE_QUERY);
    const sync = () => setWide(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  /* Ölçüm: port ve göbek merkezleri haritanın kutusuna göre. Aynı sonuç
     tekrar yazılmıyor — SVG'nin kendisi ölçülen kutuyu değiştirmediği için
     döngü yok, ama gereksiz çizimi de engelliyor. */
  const measure = useCallback(() => {
    const map = mapRef.current;
    const hubEl = hubRef.current;
    if (!map || !hubEl || !window.matchMedia(WIDE_QUERY).matches) {
      if (geoKey.current) {
        geoKey.current = '';
        setGeo(null);
      }
      return;
    }

    const box = map.getBoundingClientRect();
    const hubBox = hubEl.getBoundingClientRect();
    const ports: Port[] = [];
    map.querySelectorAll<HTMLElement>('[data-port]').forEach((el) => {
      const b = el.getBoundingClientRect();
      ports.push({
        key: el.dataset.port ?? '',
        x: round(b.left + b.width / 2 - box.left),
        y: round(b.top + b.height / 2 - box.top),
      });
    });

    const next: Geometry = {
      w: round(box.width),
      h: round(box.height),
      hub: {
        x: round(hubBox.left + hubBox.width / 2 - box.left),
        y: round(hubBox.top + hubBox.height / 2 - box.top),
        r: round(hubBox.width / 2),
      },
      ports,
    };
    const key = JSON.stringify(next);
    if (key === geoKey.current) return;
    geoKey.current = key;
    setGeo(next);
  }, []);

  useLayoutEffect(() => {
    measure();
    const map = mapRef.current;
    if (!map) return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(map);
    /* Yazı tipi geç gelirse satır boyları değişiyor; ResizeObserver haritanın
       toplam boyu değişmezse haber vermiyor (satırlar 1fr), elle tekrar ölç. */
    void document.fonts?.ready.then(measure);
    return () => observer.disconnect();
  }, [measure, wide]);

  const traces = useMemo(() => (geo ? buildTraces(geo) : []), [geo]);
  const traceByKey = useMemo(() => new Map(traces.map((trace) => [trace.key, trace])), [traces]);

  /* Paket akışı. Seçim yoksa rastgele bir hattan (aynı hat art arda değil),
     seçim varsa yalnız o hattan ve daha sık. */
  useEffect(() => {
    if (reduce || !inView || traces.length === 0) return;

    const pool = active && traceByKey.has(active) ? [active] : traces.map((trace) => trace.key);
    let last = '';
    const spawn = () => {
      let key = pool[Math.floor(Math.random() * pool.length)]!;
      if (pool.length > 1 && key === last) key = pool[(pool.indexOf(key) + 1) % pool.length]!;
      last = key;
      packetSeq.current += 1;
      const id = packetSeq.current;
      /* Sekme arka plandayken bitiş olayı gecikebilir — liste 12'de kesiliyor. */
      setPackets((current) => [...current.slice(-11), { id, key }]);
    };

    spawn();
    const timer = window.setInterval(spawn, active ? FOCUS_EVERY : AMBIENT_EVERY);
    return () => window.clearInterval(timer);
  }, [reduce, inView, traces, traceByKey, active]);

  const dropPacket = useCallback((id: number) => {
    setPackets((current) => current.filter((packet) => packet.id !== id));
  }, []);

  const pick = useCallback(
    (key: string, fromTap = false) => {
      setActive(key);
      /* Dar ekranda gösterge iki listenin arasında; dokunulan satırdan uzaksa
         en yakın kenara kaydır. Klavye odağında kaydırma yok — odak zaten
         görünür satırda, ekranı oynatmak yönünü kaybettirir. */
      if (!fromTap || wide) return;
      const panel = readoutRef.current;
      if (!panel) return;
      const box = panel.getBoundingClientRect();
      if (box.top < 0 || box.bottom > window.innerHeight) {
        panel.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
      }
    },
    [reduce, wide],
  );

  const entry = active ? (ENTRIES.get(active) ?? null) : null;
  const readoutId = `${uid}-readout`;

  return (
    <div className="imap" ref={mapRef} data-active={active ? '' : undefined}>
      {geo && (
        <svg
          className="imap-traces"
          width={geo.w}
          height={geo.h}
          viewBox={`0 0 ${geo.w} ${geo.h}`}
          aria-hidden="true"
          focusable="false"
        >
          {traces.map((trace) => (
            <path
              key={trace.key}
              className="imap-trace"
              d={trace.d}
              data-on={active === trace.key ? '' : undefined}
            />
          ))}
          {/* Yeşil katman ayrı yol: renk geçişi değil opaklık geçişi. */}
          {traces.map((trace) => (
            <path
              key={`${trace.key}-lit`}
              className="imap-trace-lit"
              d={trace.d}
              data-on={active === trace.key ? '' : undefined}
            />
          ))}
        </svg>
      )}

      <div className="imap-packets" aria-hidden="true">
        {packets.map((packet) => {
          const trace = traceByKey.get(packet.key);
          if (!trace) return null;
          const duration = packetDuration(trace.length);
          return (
            <motion.span
              key={packet.id}
              className="imap-packet"
              initial={{ x: trace.xs[0], y: trace.ys[0], opacity: 0 }}
              animate={{ x: trace.xs, y: trace.ys, opacity: [0, 1, 1, 0] }}
              transition={{
                duration,
                ease: 'linear',
                opacity: { duration, times: [0, 0.12, 0.82, 1], ease: 'linear' },
              }}
              onAnimationComplete={() => dropPacket(packet.id)}
            />
          );
        })}
      </div>

      <MapColumn
        group="ad"
        titleId={`${uid}-ads`}
        title={t('map.ads')}
        count={AD_INTEGRATIONS.length}
        active={active}
        readoutId={readoutId}
        onPick={pick}
      />

      <div className="imap-hubcol">
        <div className="imap-hub" ref={hubRef}>
          <span className="imap-hub-ring" aria-hidden="true" />
          <span className="imap-hub-ring imap-hub-ring--inner" aria-hidden="true" />
          <span className="imap-hub-tick imap-hub-tick--n" aria-hidden="true" />
          <span className="imap-hub-tick imap-hub-tick--s" aria-hidden="true" />
          {/* Seçim değişince bir kez genişleyip sönen halka: göbek o
              platformdan "aldı". Anahtarla yeniden kuruluyor; döngü değil. */}
          <motion.span
            key={active ?? 'idle'}
            className="imap-hub-ripple"
            aria-hidden="true"
            initial={{ scale: 1, opacity: active ? 0.6 : 0 }}
            animate={{ scale: 1.34, opacity: 0 }}
            transition={{ duration: dur.slow, ease: ease.out }}
          />
          <Logo variant="mark" title="" className="imap-hub-mark" />
        </div>
        <div className="imap-hub-copy">
          <p className="imap-hub-name">Mentorivo</p>
          <p className="imap-hub-sub">{t('map.hubSub')}</p>
        </div>
      </div>

      <MapColumn
        group="store"
        titleId={`${uid}-stores`}
        title={t('stores.title')}
        count={STORE_INTEGRATIONS.length}
        active={active}
        readoutId={readoutId}
        onPick={pick}
      />

      <div className="imap-readout" id={readoutId} ref={readoutRef} aria-live="polite">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={active ?? 'idle'}
            className="imap-readout-body"
            variants={readoutV}
            initial="hidden"
            animate="show"
            exit="exit"
          >
            <Readout entry={entry} />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function MapColumn({
  group,
  titleId,
  title,
  count,
  active,
  readoutId,
  onPick,
}: {
  group: 'ad' | 'store';
  titleId: string;
  title: string;
  count: number;
  active: string | null;
  readoutId: string;
  onPick: (key: string, fromTap?: boolean) => void;
}) {
  const t = useTranslations('platforms');
  const items = group === 'ad' ? AD_INTEGRATIONS : STORE_INTEGRATIONS;

  return (
    <div className={`imap-col imap-col--${group}`}>
      <div className="imap-col-head">
        <span className="imap-dot" aria-hidden="true" />
        <h3 className="imap-col-title" id={titleId}>
          {title}
        </h3>
        <span className="imap-col-count">{t('map.live', { count })}</span>
      </div>

      <ul className={`imap-list imap-list--${group}`} aria-labelledby={titleId}>
        {items.map((item) => (
          <li key={item.key} className="imap-item">
            <button
              type="button"
              className={`imap-row imap-row--${group}`}
              aria-pressed={active === item.key}
              aria-controls={readoutId}
              onPointerEnter={(event) => {
                if (event.pointerType === 'mouse') onPick(item.key);
              }}
              onFocus={() => onPick(item.key)}
              onClick={(event) => onPick(item.key, event.detail > 0)}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                className="imap-mark"
                src={item.mark}
                alt=""
                width={item.w}
                height={item.h}
                loading="lazy"
                decoding="async"
                style={{ '--mark-scale': item.scale } as CSSProperties}
              />
              <span className="imap-row-text">
                <span className="imap-row-name">
                  {group === 'ad' ? t(`items.${item.key as AdItem['key']}.name`) : item.name}
                </span>
                {group === 'ad' ? (
                  <span className="imap-row-sub">
                    {t(`items.${item.key as AdItem['key']}.scope`)}
                  </span>
                ) : null}
              </span>
              {/* Hattın başladığı nokta — ölçüm bu öğeyi okuyor. Yeşil:
                  bağlı. Seçilince çevresinde halka açılıyor (CSS). */}
              <span className="imap-port" data-port={item.key} aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Readout({ entry }: { entry: Entry | null }) {
  const t = useTranslations('platforms');

  if (!entry) {
    return (
      <>
        <div className="imap-readout-id">
          <span className="imap-dot imap-dot--lg" aria-hidden="true" />
          <div>
            <p className="imap-readout-name">{t('readout.idleTitle', { count: TOTAL })}</p>
            <p className="imap-readout-scope">{t('readout.idleHint')}</p>
          </div>
        </div>
        <p className="imap-readout-text">{t('note')}</p>
      </>
    );
  }

  const { group, item } = entry;
  const name = group === 'ad' ? t(`items.${item.key}.name`) : item.name;
  const scope = group === 'ad' ? t(`items.${item.key}.scope`) : t(`readout.kinds.${item.kind}`);
  const text = group === 'ad' ? t(`items.${item.key}.text`) : t('stores.text');

  return (
    <>
      <div className="imap-readout-id">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          className="imap-readout-mark"
          src={item.mark}
          alt=""
          width={item.w}
          height={item.h}
          decoding="async"
          style={{ '--mark-scale': item.scale } as CSSProperties}
        />
        <div>
          <p className="imap-readout-name">{name}</p>
          <p className="imap-readout-scope">{scope}</p>
        </div>
      </div>
      <p className="imap-readout-text">{text}</p>
      <dl className="imap-readout-meta">
        <div>
          <dt>{t('readout.auth')}</dt>
          <dd>{t(`readout.authModes.${item.auth}`)}</dd>
        </div>
        <div>
          <dt>{t('readout.status')}</dt>
          <dd className="imap-readout-live">
            <span className="imap-dot" aria-hidden="true" />
            {t('status.live')}
          </dd>
        </div>
      </dl>
    </>
  );
}
